import { useTskContext } from '@Hooks/useTskContext';
import {
    EmployeeDTO,
    EmployeeEmploymentRelationshipDTO,
    EmploymentDTO,
    ValidationLink,
    ValidationResult,
    ValidationResultsDTO,
} from '@Models/model.types';
import {
    ActionsCell,
    Cell,
    Input,
    Label,
    OpenNewIcon,
    Row,
    Select,
    Status,
    StatusMenu,
    StatusMenuDrawer,
    StatusMenuItem,
    StatusMenuItemIcon,
    StatusMenuItemText,
    StatusMenuOpener,
    StatusVariant,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    useDropdownOption,
} from '@tlx/atlas';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EmployeeIdToEmploymentId } from '@Page/SalaryRun/stateReducer';
import {
    ColumnId,
    EMPLOYEE_TABLE_COLUMNS_SETTING_KEY,
    getDefaultColumns,
} from '@Component/EmployeeTable/EmployeeTableToolbar/EmployeeTableColumnSettings';
import { AvatarById, useTableColumnsSetting } from '@tlx/astro-shared';
import { employeeSalaryLink } from '@Util/links';
import { DivisionDisplay } from '@Component/Table/Table.utils.tsx';
import classNames from 'classnames';
import { SalariesInPeriod } from './SalariesInPeriod';

interface CreateSalaryRowProps {
    relationship: EmployeeEmploymentRelationshipDTO;
    checked: (relationship: EmployeeIdToEmploymentId) => boolean;
    onCheck: (relationship: EmployeeIdToEmploymentId) => void;
    onChangeEmployment: (employeeToEmployment: EmployeeIdToEmploymentId) => void;
}

export const CreateSalaryRow: React.FC<CreateSalaryRowProps> = ({
    relationship,
    checked,
    onCheck,
    onChangeEmployment,
}) => {
    const { employeeIdToEmploymentId, columns, selectedIndex, selectedEmployment, onSelectAnotherEmployment } =
        useEmployeeRow({ relationship, onChangeEmployment });

    return (
        <Row>
            <EmployeeCheckbox
                checked={checked(employeeIdToEmploymentId)}
                validations={relationship.validationResults[selectedIndex]?.validations || []}
                onCheck={() => onCheck(employeeIdToEmploymentId)}
            />
            <EmployeeInformationDisplay employee={relationship.employee} />
            <EmployeeRowColumns
                columns={columns}
                renderColumn={(column) => {
                    switch (column) {
                        case 'NUMBER':
                            return <EmployeeNumberDisplay employeeNumber={relationship.employee.number.toString()} />;
                        case 'STARTDATE':
                            return (
                                <EmploymentDisplay
                                    employments={relationship.employments}
                                    onChangeEmployment={onSelectAnotherEmployment}
                                />
                            );
                        case 'DEPARTMENT':
                            return (
                                <DepartmentDisplay
                                    departmentDisplayName={relationship.employee.department?.displayName ?? ''}
                                />
                            );
                        case 'DIVISION':
                            return (
                                <Cell>
                                    <DivisionDisplay division={selectedEmployment?.division} />
                                </Cell>
                            );
                        case 'STATUS':
                            return (
                                <StatusDisplay
                                    status={
                                        relationship?.validationResults[selectedIndex] || {
                                            validations: [],
                                            warnings: [],
                                        }
                                    }
                                />
                            );
                        case 'PREVIOUS_SALARIES':
                            return (
                                <Cell>
                                    <SalariesInPeriod salaries={relationship.previousSalaries} />
                                </Cell>
                            );
                        default:
                            return null;
                    }
                }}
            />
        </Row>
    );
};

const useEmployeeRow = ({
    relationship,
    onChangeEmployment,
}: {
    relationship: EmployeeEmploymentRelationshipDTO;
    onChangeEmployment: (employeeToEmployment: EmployeeIdToEmploymentId) => void;
}) => {
    const intl = useIntl();
    const { showDepartmentIsWageAdmin, hasDivision } = useTskContext();
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const selectedEmployment = relationship.employments[selectedIndex];
    const employeeIdToEmploymentId: EmployeeIdToEmploymentId = {
        employeeId: relationship.employee.id,
        employmentId: selectedEmployment.id,
    };
    const defaultColumns = getDefaultColumns(intl, showDepartmentIsWageAdmin, hasDivision);
    const columnsFromStorage = useTableColumnsSetting(
        EMPLOYEE_TABLE_COLUMNS_SETTING_KEY,
        defaultColumns.map((column) => column.id),
    );
    //mapping to uppercase (some people have old localstorage that is lowercase) https://tripletex.slack.com/archives/C01AHR1UPFD/p1734947738139289
    const columns = columnsFromStorage.map((column) => column.toUpperCase() as ColumnId);

    const onSelectAnotherEmployment = (newIndex: number, employmentId: number) => {
        setSelectedIndex(newIndex);
        onChangeEmployment({
            employeeId: relationship.employee.id,
            employmentId: employmentId,
        });
    };

    return {
        columns,
        employeeIdToEmploymentId,
        onSelectAnotherEmployment,
        selectedEmployment,
        selectedIndex,
    };
};

const EmploymentDisplay = ({
    employments,
    onChangeEmployment,
}: {
    employments: EmploymentDTO[];
    onChangeEmployment: (newIndex: number, employmentId: number) => void;
}) => {
    const hasMultipleEmployments = employments.length > 1;
    const selectedEmployment = employments[0];
    return (
        <Cell className="atl-py-8">
            {hasMultipleEmployments ? (
                <EmploymentDropdown
                    employments={employments}
                    data-testid={'employment-dropdown'}
                    onChange={onChangeEmployment}
                />
            ) : (
                <Tooltip>
                    <TooltipTrigger>
                        <span className={'atl-truncate'}>
                            <div>
                                {selectedEmployment.startDate}
                                {selectedEmployment.endDate ? ' \u2014 ' : ''}
                            </div>
                            <div> {selectedEmployment.endDate}</div>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        {selectedEmployment.startDate}
                        {selectedEmployment.endDate ? ' \u2014 ' : ''} {selectedEmployment.endDate}
                    </TooltipContent>
                </Tooltip>
            )}
        </Cell>
    );
};

export const EmployeeRowColumns = ({
    columns,
    renderColumn,
}: {
    renderColumn: (column: ColumnId) => React.ReactNode;
    columns: ColumnId[];
}) => {
    return (
        <>
            {columns.map((column) => (
                <React.Fragment key={column}>{renderColumn(column)}</React.Fragment>
            ))}
        </>
    );
};

const EmployeeCheckbox = ({
    checked,
    validations,
    onCheck,
}: {
    checked: boolean;
    validations: ValidationLink[];
    onCheck: () => void;
}) => {
    const disabled = validations.length !== 0;
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <ActionsCell>
            <Tooltip open={showTooltip}>
                <TooltipTrigger onMouseOver={() => setShowTooltip(disabled)} onMouseOut={() => setShowTooltip(false)}>
                    <Input
                        type="checkbox"
                        data-testid="select-employee-checkbox"
                        onChange={() => onCheck()}
                        checked={checked}
                        disabled={disabled}
                    />
                </TooltipTrigger>
                <TooltipContent>
                    {validations.map((validation) => (
                        <React.Fragment key={validation?.message}>
                            {validation.linkDescription ?? validation?.message}
                            <br />
                        </React.Fragment>
                    ))}
                </TooltipContent>
            </Tooltip>
        </ActionsCell>
    );
};

const EmployeeInformationDisplay = ({ employee }: { employee: EmployeeDTO }) => {
    const intl = useIntl();
    const displayName =
        employee.displayName + (employee.isContact ? ' (' + intl.formatMessage({ id: 'text_contact' }) + ')' : '');

    const employeeRedirectLink = employeeSalaryLink(employee.id);

    return (
        <Cell className={'atl-flex atl-gap-8'} title={employee.displayName}>
            <AvatarById
                imageId={employee.pictureId === 0 ? undefined : employee.pictureId}
                name={employee.displayName}
            />
            <Label className={'atl-truncate tsk-text-underline'}>
                <a href={employeeRedirectLink}>{displayName}</a>
            </Label>
        </Cell>
    );
};

const EmployeeNumberDisplay = ({ employeeNumber }: { employeeNumber: string }) => {
    return (
        <TextCell title={employeeNumber} truncate>
            {employeeNumber || '-'}
        </TextCell>
    );
};

const EmploymentOption = ({ employment }: { employment: EmploymentDTO }) => {
    const { attributes, hasFocus, isSelected } = useDropdownOption({
        value: employment.id.toString(),
        displayName: `${employment.startDate}${employment.endDate ? ' \u2014 ' + employment.endDate : ''}`,
    });
    return (
        <div
            className={classNames('atl-dropdown__option', {
                'atl-dropdown__option--has-focus': hasFocus,
            })}
            {...attributes}
        >
            <span
                className={classNames('atl-truncate', {
                    'atl-font-medium': isSelected,
                })}
            >
                <div>
                    {employment.startDate}
                    {employment.endDate ? ' \u2014 ' : ''}
                </div>
                <div> {employment.endDate}</div>
            </span>
        </div>
    );
};

const EmploymentDropdown = ({
    employments,
    onChange,
}: {
    employments: EmploymentDTO[];
    onChange: (newIndex: number, employmentId: number) => void;
}) => {
    return (
        <Select
            data-testid={'employment-dropdown'}
            onChange={(e) => {
                onChange(e.target.selectedIndex, parseInt(e.target.value));
            }}
            className={'tsk__employment_dropdown tsk-table-row-dropdown atl-w-full'}
            defaultValue={employments[0].id.toString()}
        >
            {employments.map((employment) => (
                <EmploymentOption key={employment.id} employment={employment} />
            ))}
        </Select>
    );
};

const DepartmentDisplay = ({ departmentDisplayName }: { departmentDisplayName: string }) => {
    return (
        <Cell>
            <Tooltip placement={'bottom-start'}>
                <TooltipTrigger>
                    <span className={'atl-truncate'}>{departmentDisplayName}</span>
                </TooltipTrigger>
                <TooltipContent>{departmentDisplayName}</TooltipContent>
            </Tooltip>
        </Cell>
    );
};

function getTotalVariant(status: ValidationResultsDTO): StatusVariant {
    if (status.validations.length > 0) {
        return 'error';
    }
    return status.warnings.length > 0 ? 'warning' : 'success';
}

function getValidationResults(status: ValidationResultsDTO): ValidationResult[] {
    return [
        ...status.validations.map((validation) => ({
            validation: validation,
            variant: 'error' as const,
        })),
        ...status.warnings.map((warning) => ({
            validation: warning,
            variant: 'warning' as const,
        })),
    ];
}

const StatusDisplay = ({ status }: { status: ValidationResultsDTO }) => {
    const totalVariant = getTotalVariant(status);
    const validationResults = getValidationResults(status);
    const numberOfMessages = validationResults.length;

    return (
        <Cell className={'tsk-status'}>
            <StatusMenu>
                <StatusMenuOpener>
                    <Status variant={totalVariant}>
                        {numberOfMessages > 0 ? (
                            <FormattedMessage id={'tsk_x_missing'} values={{ 0: numberOfMessages }} />
                        ) : (
                            'OK'
                        )}
                    </Status>
                </StatusMenuOpener>
                <StatusMenuDrawer>
                    {validationResults.map((validationResult, index) => (
                        <StatusMenuItem
                            key={index}
                            onClick={
                                validationResult.validation.href
                                    ? () => {
                                          window.open(validationResult.validation.href);
                                      }
                                    : undefined
                            }
                        >
                            <Status variant={validationResult.variant} />
                            <StatusMenuItemText>{validationResult.validation.message}</StatusMenuItemText>
                            {validationResult.validation.href && (
                                <StatusMenuItemIcon>
                                    <OpenNewIcon />
                                </StatusMenuItemIcon>
                            )}
                        </StatusMenuItem>
                    ))}
                </StatusMenuDrawer>
            </StatusMenu>
        </Cell>
    );
};
